import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as qz from 'qz-tray';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class QzTrayService {
  private connectionPromise: Promise<any> | null = null;
  private isInitialized = false;

  constructor(private http: HttpClient) {
    this.setupReconnectHandler();
  }

  /* ---------------- INITIALIZATION ---------------- */

  async initQz(): Promise<void> {
    if (this.isInitialized) return;

    // Required to avoid Zone.js issues
    qz.api.setPromiseType((resolver: any) => new Promise(resolver));

    const certificate = await this.loadQzCertificate();

    qz.security.setCertificatePromise((resolve: any) => {
      resolve(certificate);
    });

    qz.security.setSignaturePromise(this.createSignaturePromise.bind(this));

    this.isInitialized = true;
  }

  private async ensureConnection(): Promise<void> {
    if (!this.isInitialized) {
      await this.initQz();
    }

    if (qz.websocket.isActive()) return;

    if (!this.connectionPromise) {
      this.connectionPromise = qz.websocket.connect().catch((err: any) => {
        this.connectionPromise = null;
        console.error('❌ QZ Tray connection failed:', err);
        throw err;
      });
    }

    await this.connectionPromise;
  }

  /* ---------------- SECURITY ---------------- */

  private createSignaturePromise(toSign: string) {
    return (resolve: (signature: string) => void, reject: (error: any) => void) => {
      this.http
        .post(
          `${environment.productionSystemApiBaseUrl}/qztray/sign`,
          { toSign },
          { responseType: 'text' },
        )
        .subscribe({
          next: (signature) => resolve(signature),
          error: (err) => reject(err),
        });
    };
  }

  private async loadQzCertificate(): Promise<string> {
    return this.http
      .get(`${environment.productionSystemApiBaseUrl}/qztray/certificate`, { responseType: 'text' })
      .toPromise()
      .then((cert) => cert ?? '');
  }

  /* ---------------- PRINTER METHODS ---------------- */

  getPrinters(): Observable<string[]> {
    return from(this.ensureConnection().then(() => qz.printers.find())).pipe(
      map((printers) => printers as string[]),
    );
  }

  printZpl(printerName: string, zpl: string): Observable<any> {
    return from(
      this.ensureConnection().then(() => {
        const config = qz.configs.create(printerName, {
          encoding: 'utf-8',
        });
        return qz.print(config, [zpl]);
      }),
    );
  }

  printZplBatch(printerName: string, zplList: string[]): Observable<any> {
    return from(
      this.ensureConnection().then(() => {
        const config = qz.configs.create(printerName, {
          encoding: 'utf-8',
          copies: 1,
        });
        return qz.print(config, zplList);
      }),
    );
  }

  printDataBatch(printerName: string, printDataArray: any[]): Observable<any> {
    return from(
      this.ensureConnection().then(() => {
        const batchPrintData = printDataArray.map((data) => ({
          type: 'raw',
          format: 'plain',
          data: Array.isArray(data) ? data.join('\n') : data,
        }));

        const config = qz.configs.create(printerName);
        return qz.print(config, batchPrintData);
      }),
    );
  }

  /* ---------------- CLEANUP ---------------- */

  disconnect(): void {
    if (qz.websocket.isActive()) {
      qz.websocket.disconnect();
    }
    this.connectionPromise = null;
    this.isInitialized = false;
  }

  private setupReconnectHandler(): void {
    qz.websocket.setClosedCallbacks(() => {
      console.warn('⚠️ QZ Tray disconnected. Reconnecting...');
      this.connectionPromise = null;
      setTimeout(() => {
        this.ensureConnection().catch((err) => console.error('Reconnect failed:', err));
      }, 2000);
    });

    qz.websocket.setErrorCallbacks((err: any) => {
      console.error('QZ Tray WebSocket error:', err);
      this.connectionPromise = null;
    });
  }
}
